<template>
  <div class="modal-overlay" @click.self="onClose">
    <div class="modal-content" :class="{ 'modal-mobile': isMobile }">
      <!-- Шапка -->
      <div class="modal-header">
        <h2>🕐 Лист ожидания</h2>
        <button class="btn-close" @click="onClose">✕</button>
      </div>
      
      <!-- Табы -->
      <div class="modal-tabs">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <span class="tab-icon">{{ tab.icon }}</span>
          <span class="tab-label">{{ tab.label }}</span>
          <span v-if="tab.count > 0" class="tab-badge">{{ tab.count }}</span>
        </button>
      </div>
      
      <!-- Контент -->
      <div class="modal-body">
        <!-- Таб: Активные -->
<div v-if="activeTab === 'active'" class="tab-content">
  <div v-if="waitlist.isLoading" class="loading-state">
    <span class="spinner"></span>
    <span>Загрузка заявок...</span>
  </div>
  
  <div v-else-if="safeActiveEntries.length === 0" class="empty-state">
    <div class="empty-icon">📭</div>
    <div class="empty-title">Нет активных заявок</div>
    <div class="empty-hint">Заявки из виджета появятся здесь</div>
  </div>
  
  <div v-else class="entries-list">
    <WaitlistCard
      v-for="entry in safeActiveEntries" 
      :key="entry.id"
      :entry="entry"
      @offer-slot="handleOfferSlot"
      @delete-entry="handleDeleteEntry"
      @cancel-offer="handleCancelOffer" 
    />
  </div>
</div>
        
        <!-- Таб: Предложенные слоты -->
<div v-if="activeTab === 'offered'" class="tab-content">
  <div v-if="waitlist.isLoading" class="loading-state">
    <span class="spinner"></span>
    <span>Загрузка...</span>
  </div>
  
  <div v-else-if="safeOfferedEntries.length === 0" class="empty-state">
    <div class="empty-icon">📤</div>
    <div class="empty-title">Нет предложенных слотов</div>
    <div class="empty-hint">Здесь будут заявки, которым вы предложили слот</div>
  </div>
  
  <div v-else class="entries-list">
    <WaitlistCard
      v-for="entry in safeOfferedEntries" 
      :key="entry.id"
      :entry="entry"
      @offer-slot="handleOfferSlot"
      @delete-entry="handleDeleteEntry"
      @cancel-offer="handleCancelOffer" 
    />
  </div>
</div>
        
<!-- Таб: История -->
<div v-if="activeTab === 'history'" class="tab-content">
  <div v-if="waitlist.isLoading" class="loading-state">
    <span class="spinner"></span>
    <span>Загрузка истории...</span>
  </div>
  
  <div v-else-if="safeHistoryEntries.length === 0" class="empty-state">
    <div class="empty-icon">📜</div>
    <div class="empty-title">История пуста</div>
    <div class="empty-hint">Завершённые и отклонённые заявки появятся здесь</div>
  </div>
  
  <div v-else class="entries-list">
    <WaitlistCard
      v-for="entry in safeHistoryEntries" 
      :key="entry.id"
      :entry="entry"
      compact
      @offer-slot="handleOfferSlot"
      @delete-entry="handleDeleteEntry"
      @cancel-offer="handleCancelOffer"
    />
  </div>
</div>
      </div>
    </div>
    
    <!-- Модалка предложения слота -->
    <OfferSlotModal
      v-if="showOfferModal"
      :entry="selectedEntry"
      @close="closeOfferModal"
      @offered="onSlotOffered"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useWaitlistStore } from '../stores/waitlist'
import WaitlistCard from './WaitlistCard.vue'
import OfferSlotModal from './OfferSlotModal.vue'

const emit = defineEmits(['close'])

const waitlist = useWaitlistStore()
const activeTab = ref('active')
const isMobile = ref(false)
const showOfferModal = ref(false)
const selectedEntry = ref(null)

// Безопасные computed с fallback на пустой массив
const safeActiveEntries = computed(() => waitlist.activeEntries || [])
const safeOfferedEntries = computed(() => waitlist.offeredEntries || [])
const safeHistoryEntries = computed(() => waitlist.historyEntries || [])

const tabs = computed(() => [
  { id: 'active', label: 'Активные', icon: '📋', count: safeActiveEntries.value.length },
  { id: 'offered', label: 'Предложенные', icon: '📤', count: safeOfferedEntries.value.length },
  { id: 'history', label: 'История', icon: '📜', count: safeHistoryEntries.value.length }
])

function checkMobile() {
  isMobile.value = window.innerWidth <= 768
}

onMounted(async () => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  await waitlist.loadAllStatuses()
  await waitlist.loadCounts()
})

function onClose() {
  emit('close')
}

function handleOfferSlot(entry) {
  selectedEntry.value = entry
  showOfferModal.value = true
}

function closeOfferModal() {
  showOfferModal.value = false
  selectedEntry.value = null
}

async function onSlotOffered() {
  await waitlist.loadAllStatuses()
  await waitlist.loadCounts()
  closeOfferModal()
}

async function handleCancelOffer(id) {
  if (!confirm('Отменить предложенный слот? Заявка вернётся в активные.')) return
  try {
    const { api } = await import('../stores/auth')
    await api.post(`/waitlist/${id}/decline`)
    await waitlist.loadAllStatuses()
    await waitlist.loadCounts()
  } catch (err) {
    alert('Ошибка: ' + (err.response?.data?.error || err.message))
  }
}

async function handleDeleteEntry(id) {
  if (!confirm('Удалить заявку?')) return
  try {
    await waitlist.remove(id)
    await waitlist.loadAllStatuses()
    await waitlist.loadCounts()
  } catch (err) {
    alert('Ошибка: ' + (err.response?.data?.error || err.message))
  }
}

</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalIn 0.25s ease-out;
}

@keyframes modalIn {
  from { opacity: 0; transform: translateY(20px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.2rem;
  color: #1e293b;
  font-weight: 600;
}

.btn-close {
  width: 36px;
  height: 36px;
  background: #f1f5f9;
  color: #64748b;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #e2e8f0;
  color: #1e293b;
}

/* Табы */
.modal-tabs {
  display: flex;
  gap: 0;
  padding: 0 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  overflow-x: auto;
}

.modal-tabs button {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.875rem 1.25rem;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: #64748b;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  font-weight: 500;
}

.modal-tabs button:hover {
  color: #1e293b;
  background: #f8fafc;
}

.modal-tabs button.active {
  color: var(--brand-primary);
  border-bottom-color: var(--brand-primary);
  background: var(--brand-primary-soft);
}

.tab-icon {
  font-size: 1rem;
}

.tab-badge {
  background: #e2e8f0;
  color: #475569;
  padding: 0.1rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}

.modal-tabs button.active .tab-badge {
  background: var(--brand-primary);
  color: white;
}

/* Тело */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  min-height: 0;
}

.tab-content {
  height: 100%;
}

/* Список заявок */
.entries-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

/* Состояния */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem;
  color: #6b7280;
  font-size: 0.95rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: var(--brand-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

.empty-title {
  font-size: 1rem;
  color: #4b5563;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.empty-hint {
  font-size: 0.85rem;
  color: #9ca3af;
}

/* Мобильная */
.modal-mobile {
  max-width: 100% !important;
  margin: 0.5rem !important;
  border-radius: 12px !important;
  max-height: 95vh !important;
}

.modal-mobile .modal-header {
  padding: 1rem;
}

.modal-mobile .modal-tabs {
  padding: 0 1rem;
}

.modal-mobile .modal-tabs button {
  padding: 0.75rem 0.875rem;
  font-size: 0.85rem;
}

.modal-mobile .modal-body {
  padding: 0.75rem 1rem;
}

@media (max-width: 520px) {
  .tab-label {
    display: none;
  }
  
  .modal-tabs button {
    padding: 0.75rem;
  }
}
</style>